import { AuthContext } from 'contexts/AuthContext';
import Nodatafound from 'pages/Common/Nodatafound';
import UpdatePasswordModal from 'pages/Model/UpdatePasswordModal';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FaRegEdit } from 'react-icons/fa';
import { ThreeDots } from 'react-loader-spinner';

const UpdateUserPasswordList = ({
  fetchUsers,
  updateUserPassword,
  userType,
  columns,
}) => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filter, setFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    userId: null,
    password: '',
    confirmPassword: '',
    name: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { state } = useContext(AuthContext);

  function reset() {
    setFilteredData(data);
    setSearchTerm('');
    setFilter('all');
    setSortOrder('');
    setRoleFilter('all');
  }
  const selectedBranch = state?.selectedBranch;

  async function fetchUserData() {
    setLoading(true);
    try {
      const response = await fetchUsers(state?.user?.id, selectedBranch);
      if (response.data.success) {
        const processedData = response.data.data.map((user) => ({
          ...user,
          branchName: user?.branch || '-',
          branchLocation: user?.location || '-',
        }));
        setData(processedData);
        setFilteredData(processedData);
        setLoading(false);
        if (processedData.length === 0) {
          toast.error(`No data found for the ${userType}s`);
        }
      }
    } catch (error) {
      setLoading(false);
      if (error?.response?.data?.message == 'Teachers data not found!') {
        setData([]);
        setFilteredData([]);
      }
      console.error(error?.response?.data?.message, 'error in fetchUserData');
      toast.error(error.response.data.message);
    }
  }

  useEffect(() => {
    fetchUserData();
  }, [selectedBranch]);

  useEffect(() => {
    const sortedData = [...filteredData].sort((a, b) => {
      const compareValue = a.name.localeCompare(b.name);
      return sortOrder === 'asc' ? compareValue : -compareValue;
    });
    setFilteredData(sortedData);
  }, [sortOrder]);

  useEffect(() => {
    let result = data;
    if (searchTerm) {
      result = result.filter(
        (user) =>
          user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (user.contact &&
            user.contact.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (filter !== 'all') {
      result = result.filter((user) => user.branchName === filter);
    }
    if (roleFilter !== 'all') {
      result = result.filter((user) => user.role === roleFilter);
    }

    if (sortOrder) {
      result.sort((a, b) => {
        const compareValue = a.name.localeCompare(b.name);
        return sortOrder === 'asc' ? compareValue : -compareValue;
      });
    }

    setFilteredData(result);
  }, [data, filter, roleFilter, sortOrder, searchTerm]);

  const handleUpdatePassword = async (event) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    event.preventDefault();

    if (passwordData.password !== passwordData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (passwordData.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    try {
      const response = await updateUserPassword({
        userId: passwordData.userId,
        password: passwordData.password,
      });
      if (response.data.success) {
        toast.success(response.data.message);
        setShowUpdateModal(false);
        setPasswordData({
          userId: null,
          password: '',
          confirmPassword: '',
          name: '',
        });
        fetchUserData();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          `Failed to update password for ${userType}`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to format role display
  const formatRoleDisplay = (role) => {
    if (!role) return '-';

    // Special cases
    if (role === 'account') return 'Accountant';
    if (role === 'placement') return 'Placement Officer';

    // For all other roles, capitalize first letter
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  return (
    <div className="mt-4 h-full w-full md:mt-4">
      {loading ? (
        <div className="flex h-40 items-center justify-center ">
          <ThreeDots color="#5431ed" className="mt-100" />
        </div>
      ) : (
        <>
          <div className="flex flex-col items-center gap-4 sm:flex-row">
            <div className="w-full rounded-xl border border-gray-200 bg-lightPrimary py-2.5 pl-2.5 pr-3 text-sm font-normal text-gray-600 outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white sm:w-full md:w-full">
              <input
                type="text"
                placeholder="Search by Name, Email, or Contact"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full rounded-full bg-lightPrimary text-base text-navy-700 outline-none dark:!bg-navy-800 dark:text-white"
              />
            </div>

            <div className="w-full sm:w-[300px]"></div>
            <div className="w-full sm:w-[300px]">
              <div className="w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal text-gray-600 outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white sm:w-full md:w-full">
                <select
                  className="text-lightFourth w-full rounded-xl pr-4 text-sm outline-none dark:!bg-navy-800 dark:text-white"
                  name=""
                  id="color"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                >
                  <option value="">Select Order</option>
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </select>
              </div>
            </div>
            {state.user.role === 'superadmin' && (
              <div className="w-full sm:w-[300px]">
                <div className="w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal text-gray-600 outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white sm:w-full md:w-full">
                  <select
                    className="text-lightFourth w-full rounded-xl pr-4 text-sm outline-none dark:!bg-navy-800 dark:text-white"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                  >
                    <option value="all">All Branches</option>
                    {[...new Set(data.map((user) => user.branchName))]
                      .sort((a, b) => a.localeCompare(b))
                      .map((branch) => (
                        <option key={branch} value={branch}>
                          {branch}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            )}
            <div className="w-full sm:w-[300px]">
              <div className="w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal text-gray-600 outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white sm:w-full md:w-full">
                <select
                  className="text-lightFourth w-full rounded-xl pr-4 text-sm outline-none dark:!bg-navy-800 dark:text-white"
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  <option value="all">All Roles </option>
                  {[...new Set(data.map((user) => user.role))]
                    .sort()
                    .map((role) => (
                      <option key={role} value={role}>
                        {formatRoleDisplay(role)}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div className="w-full sm:w-[200px]">
              <button
                onClick={reset}
                className=" w-40 rounded-xl bg-brand-500 py-3 pl-2.5 pr-3 text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
              >
                Reset
              </button>
            </div>
          </div>
          <div className="mt-[14px] h-full w-full overflow-x-scroll 2xl:overflow-x-hidden">
            <table className="w-full font-dm">
              <thead className="w-full">
                <tr className="items-center border-b border-gray-200 dark:border-white/10">
                  <th className="px-[27px] pb-[12px] pt-[32px] text-start text-xs tracking-wide text-gray-600">
                    <div className="text-xs font-bold">SR. NO.</div>
                  </th>
                  {columns.map((column, index) => (
                    <th
                      key={index}
                      className="px-[27px] pb-[12px] pt-[32px] text-start text-xs tracking-wide text-gray-600"
                    >
                      <div className="text-xs font-bold">
                        {column.header.toUpperCase()}
                      </div>
                    </th>
                  ))}
                  <th className="px-[27px] pb-[12px] pt-[32px] text-start text-xs tracking-wide text-gray-600">
                    <div className="text-xs font-bold">ACTION</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length > 0 ? (
                  filteredData.map((row, index) => (
                    <tr
                      className="items-center border-b border-gray-200 dark:border-white/10"
                      key={index}
                    >
                      <td className="mt-[20px] px-[27px] py-[16px]">
                        <div className="w-full text-start font-medium text-navy-700 dark:text-white">
                          {index + 1}
                        </div>
                      </td>
                      {columns.map((column, colIndex) => (
                        <td
                          key={colIndex}
                          className="mt-[20px] px-[27px] py-[16px]"
                        >
                          <div className="w-full text-start font-medium text-navy-700 dark:text-white">
                            {column.key === 'role'
                              ? formatRoleDisplay(row[column.key])
                              : row[column.key] || '-'}
                          </div>
                        </td>
                      ))}
                      <td className="mt-[20px] px-4 py-[16px]">
                        <div className="flex w-full justify-start space-x-4">
                          <button
                            onClick={() => {
                              setPasswordData({
                                userId: row._id,
                                password: '',
                                confirmPassword: '',
                                name: row.name,
                              });
                              setShowUpdateModal(true);
                              setModalTitle(`Update ${row.name}'s Password`);
                            }}
                            className="flex items-center justify-center rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                          >
                            <FaRegEdit />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={columns.length + 2}
                      className="py-4 text-center text-gray-500"
                    >
                      <p className="text-lg font-bold text-navy-700 dark:text-white">
                        <p className="mt-0 text-center">
                          <Nodatafound title={`No ${userType} data found`} />
                        </p>
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {showUpdateModal && (
            <UpdatePasswordModal
              passwordData={passwordData}
              setPasswordData={setPasswordData}
              setShowUpdateModal={setShowUpdateModal}
              handleUpdatePassword={handleUpdatePassword}
              title={modalTitle}
              setIsSubmitting={setIsSubmitting}
              isSubmitting={isSubmitting}
            />
          )}
        </>
      )}
    </div>
  );
};

export default UpdateUserPasswordList;
